import React, { useEffect, useState } from 'react'
import { Button, DatePicker, Form, Space, Switch, Table, Tooltip } from 'antd'
import PropTypes from 'prop-types'
import { useStores } from '../../cls/stores/MobXProvider'
import { translate, useSmartTranslation } from '../../cls/TranslationContext'
/**
 * Defines a mapping object for user-friendly labels corresponding to each permission key.
 * This mapping allows the use of descriptive labels in the UI while keeping the keys intact
 * for backend processing and data handling.
 */
const permissionLabels = {
	// TODO: add translates
	roles: {
		superAdmin: translate('single.super-admin'),
		admin: translate('single.admin'),
		clientAdmin: translate('_client.admin'),
		client: translate('_client'),
		teamAdmin: translate('single.team-admin'),
		team: translate('_team-member')
	},
	lst: {
		sprints: translate('_sprint.multiple'),
		sprints_team: translate('_sprint.team'),
		requests: translate('_request'),
		meetings: translate('_meeting.multiple'),
		workflows: translate('_workflow.multiple'),
		monthly_reports: translate('_monthly-report.multiple'),
		notifications: translate('_notification.multiple'),
		documentation: translate('_documentation'),
		users: translate('_user.multiple'),
		recruitment: translate('_recruitment'),
		secured: translate('single.secured.label')
	},
	resources: {
		switchApp: translate('single.swich-app'),
		videoMeeting: translate('single.video-meeting'),
		upload_photos: translate('single.upload-photos'),
		upload_files: translate('single.upload-files'),
		chat: translate('single.chat-feature'),
		ai: translate('single.ai-assistance')
	}
}

/**
 * Permission component renders a table for managing user permissions.
 * Displays allowed features or categories along with their respective permissions.
 * Each permission can be toggled or set with a date using a Switch or DatePicker, respectively.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.data - The data object containing allowed permissions.
 * @param {Array<string>} props.permissions - An array of permission names to display.
 * @param {'categories' | 'lst' | 'roles' | 'resources' | 'notifications'} props.type - The type of data to display - can be 'categories', 'lst', 'resources' and 'roles'.
 * @returns {JSX.Element} The rendered table displaying features/categories and their permissions.
 */
const PermissionsInput = ({
	data,
	permissions,
	type,
	formIsDisabled,
	...props
}) => {
	const { t, i18n } = useSmartTranslation()

	const { form } = { ...props }
	const { users, categories } = useStores()
	const { loggedInUser } = users
	const isSuperAdmin = loggedInUser.isSuperAdmin

	//  State to manage all permission active and inactive
	const [allActive, setAllActive] = useState(true)

	//  State to manage all permission active and inactive (only single row)
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [updatedKey, setUpdatedKey] = useState(null)

	/**
	 * Checks if any permissions are active (true) in the form values.
	 *
	 * This function analyzes the permissions stored in the form for the given type.
	 * It specifically handles different types of permissions: resources, notifications,
	 * and others. Depending on the type, it checks for active permissions and returns
	 * a boolean indicating whether at least one permission is true.
	 *
	 * @returns {boolean} - Returns true if any permission is active, otherwise false.
	 */
	const checkAllActive = () => {
		// Retrieve the current permissions for the specified type from the form
		const currentPermissions = form?.getFieldValue(['allowed', type])

		if (currentPermissions) {
			// Check for active permissions based on the type
			if (type === 'resources') {
				// For resources, check if any resource permission is set to true
				return Object.values(currentPermissions).some(
					(value) => value === true
				)
			} else if (type === 'notifications') {
				// For notifications, check if any notification category has any permission set to true
				return Object.values(currentPermissions).some((category) =>
					Object.values(category).some((notification) =>
						Object.values(notification).some(
							(permValue) => permValue === true
						)
					)
				)
			} else {
				// For other types, check if any category has any permission set to true
				return Object.values(currentPermissions).some((category) =>
					Object.values(category).some((value) => value === true)
				)
			}
		}

		return false
	}

	// Effect hook that runs whenever the data, permissions, or type change.
	useEffect(() => {
		if (type !== 'roles') {
			// Update the state to reflect the presence of active permissions
			const hasActivePermissions = checkAllActive()
			setAllActive(hasActivePermissions)
		}
	}, [data, permissions, type])

	/**
	 * Handles toggle all to permissions.
	 *
	 * This function is triggered when a toggle button is clicked, updating the state
	 * of permissions based on the type of permissions being modified (resources,
	 * notifications, or others). It sets all relevant permissions to either
	 * checked or unchecked based on the user's action and updates the form
	 * state accordingly.
	 *
	 * It updates the form's values using `form.setFieldsValue` with the new
	 * permission structure
	 */
	const handleToggleAll = () => {
		const newChecked = !allActive
		setAllActive(newChecked)

		// Helper function to update the form fields with new permission values
		const updateFormValues = (newValues) => {
			form.setFieldsValue({
				allowed: {
					[type]: newValues
				}
			})
		}

		// initialize new permissions object
		let allPermissions = {}

		// Handle permissions based on the type
		if (type === 'resources') {
			// For resources, set each resource's permission to the new checked value
			allPermissions = Object.keys(data).reduce((acc, key) => {
				acc[key] = newChecked
				return acc
			}, {})
		} else if (type === 'notifications') {
			// Handle permissions for notifications
			allPermissions = Object.keys(data).reduce((acc, category) => {
				acc[category] = {} // Initialize category object
				Object.keys(data[category]).forEach((notificationType) => {
					acc[category][notificationType] = {}
					// Set each permission for the notification type to newChecked
					permissions.forEach((permission) => {
						acc[category][notificationType][permission] = newChecked
					})
				})
				return acc
			}, {})
		} else {
			// For other types, set permissions dynamically based on the keys in data
			allPermissions = Object.keys(data).reduce((acc, key) => {
				acc[key] = permissions.reduce((permAcc, permission) => {
					// Exclude 'start' and 'end' permissions
					if (permission !== 'start' && permission !== 'end') {
						permAcc[permission] = newChecked // Set permission to newChecked
					}
					return permAcc
				}, {})
				return acc
			}, {})
		}

		// Update the form with the newly constructed permissions
		updateFormValues(allPermissions)
	}

	/**
	 * Handles toggle all to permissions of a specific module
	 */
	/**
	 * handleToggleRow toggles all permissions for a single row/module identified by `rowKey`.
	 * For 'notifications' it flips both 'show' and 'mail' for every event type; otherwise flips each listed permission.
	 *
	 * @function
	 * @private
	 * @param {string} rowKey – The key of the row (feature or category) to toggle.
	 */
	const handleToggleRow = (rowKey) => {
		// Get current permissions from the form or fallback to data
		const currentPermissions = form.getFieldValue(['allowed', type]) || data

		// Determine if any permission is currently checked
		let isAnyChecked = false

		if (type === 'notifications') {
			isAnyChecked = Object.values(currentPermissions[rowKey]).some(
				(event) => Object.values(event).some((value) => value === true)
			)
		} else {
			isAnyChecked = permissions.some(
				(permission) => currentPermissions[rowKey]?.[permission]
			)
		}

		// Toggle all permissions based on the current state
		const newChecked = !isAnyChecked

		// Update the permissions for the specific row
		const updatedPermissions = {
			...currentPermissions
		}

		if (type === 'notifications') {
			// For notifications, set show and mail for each event type
			updatedPermissions[rowKey] = Object.keys(
				currentPermissions[rowKey]
			).reduce((acc, eventKey) => {
				acc[eventKey] = {
					show: newChecked,
					mail: newChecked
				}
				return acc
			}, {})
		} else {
			updatedPermissions[rowKey] = permissions.reduce(
				(acc, permission) => {
					acc[permission] = newChecked // Set each permission to newChecked
					return acc
				},
				{}
			)
		}

		// Update the form with the new permissions
		form.setFieldsValue({
			allowed: {
				[type]: updatedPermissions
			}
		})

		// Update the state to force a re-render
		setUpdatedKey(newChecked)
	}

	let dataSource = []
	let columns = []
	if (type != 'notifications') {
		let permissionTitle = t('single.feature.label')
		switch (type) {
			case 'categories':
				permissionTitle = t('single.category')
				break
			case 'resources':
				permissionTitle = t('single.resource')
				break
			case 'roles':
				permissionTitle = t('single.role')
				break
		}

		// Create data source for the table from the allowed permissions
		dataSource = data
			? Object.keys(data).map((key) => {
					const item = data[key]
					return {
						key,
						feature:
							type == 'categories'
								? categories[key][i18n.language]
								: permissionLabels[type][key], // Use readable label
						permissions: permissions.map((permission) => ({
							name: permission,
							checked: item[permission] || item
						}))
					}
			  })
			: []

		// Define the columns for the table
		columns = [
			{
				title: permissionTitle,
				dataIndex: 'feature',
				key: 'feature'
			},
			...permissions.map((permission) => ({
				title: t(`single.${permission}`),
				dataIndex: permission,
				key: permission,
				render: (text, record) => {
					let fieldName = getFormFieldName(record)
					if (type == 'categories' || type == 'lst') {
						fieldName.push(permission)
					}

					// make some specific fields (admin & superAdmin) disable if loggedIn user is not an admin
					const disabled =
						(!isSuperAdmin &&
							fieldName.some((val) =>
								['admin', 'superAdmin'].includes(val)
							)) ||
						formIsDisabled
					const tooltipTitle = translate('__denied.modify%_setting')

					// Render DatePicker for start and end date permissions and switch for bool type permissions
					if (permission === 'start' || permission === 'end') {
						return (
							<Tooltip title={disabled ? tooltipTitle : ''}>
								<span>
									<Form.Item
										name={[
											'allowed',
											type,
											record.key,
											permission
										]}
										noStyle
									>
										<DatePicker
											placeholder={t(
												'__select.item%single.date'
											)}
											format="YYYY-MM-DD"
											disabled={disabled}
										/>
									</Form.Item>
								</span>
							</Tooltip>
						)
					} else {
						return (
							<Tooltip title={disabled ? tooltipTitle : ''}>
								<span>
									<Form.Item
										name={fieldName}
										valuePropName="checked"
										noStyle
									>
										<Switch
											disabled={disabled}
											defaultChecked={text}
										/>
									</Form.Item>
								</span>
							</Tooltip>
						)
					}
				}
			}))
		]

		// Render button to toggle a specific row permissions on single click(Only for lst)
		if (type === 'lst') {
			columns.splice(1, 0, {
				title: t('_action'),
				dataIndex: 'action',
				key: 'action',
				render: (text, record) => {
					const currentRowPermissions =
						form.getFieldValue(['allowed', type])?.[record.key] ||
						data[record.key] ||
						{}
					const isAnyChecked = permissions.some(
						(permission) => currentRowPermissions[permission]
					)
					const buttonText = isAnyChecked
						? t('single.all-disabled')
						: t('single.all-active')

					return (
						<Button
							disabled={formIsDisabled}
							onClick={() => handleToggleRow(record.key)}
						>
							{buttonText}
						</Button>
					)
				}
			})
		}
	} else {
		// Create data source for the table from the allowed permissions
		dataSource = data ? transformNotifications(data, permissions) : []

		// Define the columns for the table
		columns = [
			{
				title: t('_table'),
				dataIndex: 'category',
				key: 'category',
				onCell: (row, index) => {
					const currentCategory = dataSource[index].category
					const sameCategoryRows = dataSource.filter(
						(item) => item.category === currentCategory
					).length
					const isFirstRow =
						index ===
						dataSource.findIndex(
							(item) => item.category === currentCategory
						)

					return {
						rowSpan: isFirstRow ? sameCategoryRows : 0
					}
				},
				render: (categoryKey) => t(`table.${categoryKey}`) // Keep simple render for display purposes
			},
			{
				title: t('_action'),
				dataIndex: 'action',
				key: 'action',
				onCell: (row, index) => {
					const currentCategory = dataSource[index].category
					const sameCategoryRows = dataSource.filter(
						(item) => item.category === currentCategory
					).length
					const isFirstRow =
						index ===
						dataSource.findIndex(
							(item) => item.category === currentCategory
						)

					return {
						rowSpan: isFirstRow ? sameCategoryRows : 0
					}
				},
				render: (value, record) => {
					const currentRowPermissions =
						form.getFieldValue([
							'allowed',
							'notifications',
							record.category
						]) || data[record.category]
					const isAnyChecked = Object.values(
						currentRowPermissions
					).some((event) => event.show || event.mail)
					const buttonText = isAnyChecked
						? t('single.all-disabled')
						: t('single.all-active')

					return (
						<Button
							disabled={formIsDisabled}
							onClick={() => handleToggleRow(record.category)}
						>
							{buttonText}
						</Button>
					)
				}
			},
			{
				title: t('single.event'),
				dataIndex: 'event',
				key: 'event',
				render: (eventKey) => t(`single.${eventKey}`)
			},
			...permissions.map((permission) => ({
				title: t(`single.${permission}`),
				dataIndex: permission,
				key: permission,
				render: (text, record) => {
					let fieldName = getFormFieldName({ key: record.category })
					fieldName.push(record.event, permission)

					const tooltipTitle = t('__denied.modify%_setting')

					return (
						<Tooltip title={formIsDisabled ? tooltipTitle : ''}>
							<span>
								<Form.Item
									name={fieldName}
									valuePropName="checked"
									noStyle
								>
									<Switch disabled={formIsDisabled} />
								</Form.Item>
							</span>
						</Tooltip>
					)
				}
			}))
		]
	}

	// build dynamic form name
	/**
	 * getFormFieldName builds the `name` prop array for an AntD Form.Item based on the record.
	 * It ensures that 'admin' and 'superAdmin' map to a top-level path,
	 * while other types include the `type` in the path.
	 *
	 * @function
	 * @private
	 * @param {Object} record – Table row record with at least a `key` property.
	 * @returns {string[]} – Array path for `Form.Item name`.
	 */
	const getFormFieldName = (record) => {
		const isAdminOrSuperAdmin =
			record.key === 'admin' || record.key === 'superAdmin'
		return isAdminOrSuperAdmin
			? ['allowed', record.key]
			: ['allowed', type, record.key]
	}

	return (
		<Space style={{ width: '100%' }} direction="vertical">
			{type !== 'roles' && (
				<div>
					<Button
						type="primary"
						onClick={handleToggleAll}
						disabled={formIsDisabled}
					>
						{allActive
							? t('single.all-disabled')
							: t('single.all-active')}
					</Button>
				</div>
			)}
			<Table
				dataSource={dataSource}
				columns={columns}
				pagination={false}
			/>
		</Space>
	)
}

PermissionsInput.propTypes = {
	data: PropTypes.object.isRequired,
	permissions: PropTypes.array.isRequired,
	type: PropTypes.oneOf([
		'categories',
		'lst',
		'roles',
		'resources',
		'notifications'
	]).isRequired,
	formIsDisabled: PropTypes.bool
}

export default PermissionsInput

/**
 * Transforms the notifications object into a structured format suitable for table display.
 *
 * @param {Object} notifications - The notifications object containing categories and their corresponding events.
 * Each category contains events with associated permission settings.
 *
 * @param {Array} permissions - An array of permission names to be dynamically included in the row data.
 *
 * @returns {Array} An array of objects representing each notification event, structured for table rendering.
 * Each object contains:
 *   - key: A unique identifier for the row, currently using the category name.
 *   - category: The name of the category.
 *   - event: The name of the event.
 *   - permissions: An array of permission objects for the event.
 *
 * Example Input:
 * const notifications = {
 *   sprints: {
 *     new: { show: true, mail: false },
 *     update: { show: true, mail: true },
 *     progress: { show: false, mail: true }
 *   }...
 * };
 *
 * const permissions = ['show', 'mail'];
 */
const transformNotifications = (notifications, permissions) => {
	const result = []
	Object.keys(notifications).forEach((category) => {
		const events = Object.keys(notifications[category])
		events.forEach((event) => {
			const rowData = {
				key: `${category}-${event}`,
				category: category,
				event: event
			}

			// Dynamically populate permissions
			permissions.forEach((permission) => permission)

			result.push(rowData)
		})
	})
	return result
}
